import React, { useState } from 'react';
import { Thermometer, AlertTriangle } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

// Ecarts approximatifs par rapport a la temperature rectale (reference la plus fiable
// chez le nourrisson), d'apres les recommandations pediatriques usuelles.
const SITE_OFFSET_FROM_RECTAL = {
  rectal: 0,
  orale: -0.5,
  axillaire: -1.0,
  tympanique: 0,
};

const SITE_TKEYS = {
  rectal: 'fever_site_rectal',
  orale: 'fever_site_oral',
  axillaire: 'fever_site_axillary',
  tympanique: 'fever_site_tympanic',
};

const feverBand = (rectalEquiv, t) => {
  if (rectalEquiv < 37.5) return { label: t('fever_band_normal'), color: '#10B981' };
  if (rectalEquiv < 38) return { label: t('fever_band_low'), color: '#10B981' };
  if (rectalEquiv < 39) return { label: t('fever_band_fever'), color: '#F59E0B' };
  return { label: t('fever_band_high'), color: '#EF4444' };
};

const FeverConverter = () => {
  const { t } = useLanguage();
  const [value, setValue] = useState('');
  const [site, setSite] = useState('rectal');
  const [ageMonths, setAgeMonths] = useState('');

  const v = parseFloat(value);
  const valid = v > 30 && v < 45;
  const rectalEquiv = valid ? v - SITE_OFFSET_FROM_RECTAL[site] : null;
  const band = rectalEquiv !== null ? feverBand(rectalEquiv, t) : null;

  const age = parseFloat(ageMonths);
  let advice = null;
  if (rectalEquiv !== null && !Number.isNaN(age)) {
    if (age < 3 && rectalEquiv >= 38) {
      advice = { text: t('fever_advice_under3'), urgent: true };
    } else if (age < 6 && rectalEquiv >= 38.3) {
      advice = { text: t('fever_advice_under6'), urgent: false };
    } else if (age >= 6 && rectalEquiv >= 39.4) {
      advice = { text: t('fever_advice_over6'), urgent: false };
    } else if (rectalEquiv >= 38) {
      advice = { text: t('fever_advice_general'), urgent: false };
    }
  }

  return (
    <ToolPageLayout
      title={t('fever_title')}
      subtitle={t('fever_subtitle')}
      specialty="pediatre"
    >
      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div>
          <Label className="mb-1 block">{t('fever_temp_label')}</Label>
          <Input
            type="number"
            min="30"
            max="45"
            step="0.1"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Ex: 38.2"
            className="rounded-xl"
          />
        </div>
        <div>
          <Label className="mb-1 block">{t('fever_site_label')}</Label>
          <select
            value={site}
            onChange={(e) => setSite(e.target.value)}
            className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
          >
            {Object.entries(SITE_TKEYS).map(([key, tKey]) => (
              <option key={key} value={key}>{t(tKey)}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-6">
        <Label className="mb-1 block">{t('fever_age_label')}</Label>
        <Input
          type="number"
          min="0"
          max="216"
          value={ageMonths}
          onChange={(e) => setAgeMonths(e.target.value)}
          placeholder={t('fever_age_placeholder')}
          className="rounded-xl max-w-xs"
        />
      </div>

      {band && (
        <div className="rounded-2xl p-6 text-center border" style={{ borderColor: `${band.color}40`, backgroundColor: `${band.color}0D` }}>
          <Thermometer className="w-6 h-6 mx-auto mb-2" style={{ color: band.color }} />
          <div className="text-sm text-[#64748B] mb-1">{t('fever_equiv_label')}</div>
          <div className="text-3xl font-bold" style={{ color: band.color }}>{rectalEquiv.toFixed(1)}°C</div>
          <div className="mt-1 font-semibold" style={{ color: band.color }}>{band.label}</div>
        </div>
      )}

      {advice && (
        <div className={`mt-6 rounded-2xl p-4 flex gap-3 border ${advice.urgent ? 'bg-[#FEE2E2] border-[#EF4444]/30' : 'bg-[#FEF3C7] border-[#F59E0B]/30'}`}>
          <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${advice.urgent ? 'text-[#EF4444]' : 'text-[#F59E0B]'}`} />
          <p className={`text-sm ${advice.urgent ? 'text-[#991B1B]' : 'text-[#92400E]'}`}>{advice.text}</p>
        </div>
      )}

      <p className="mt-6 text-sm text-[#64748B]">
        {t('fever_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default FeverConverter;
